import { z } from 'zod';

/** Organization roles (BetterAuth organization plugin). */
export const orgRole = z.enum(['owner', 'admin', 'member']);
export type OrgRole = z.infer<typeof orgRole>;

/** Project roles (DESIGN §2). */
export const projectRole = z.enum(['manager', 'editor', 'reviewer', 'partner', 'client']);
export type ProjectRole = z.infer<typeof projectRole>;

/** From most to least powerful; used to report one "role" when a caller holds several grants. */
export const projectRoleRank: Record<ProjectRole, number> = {
  manager: 5,
  editor: 4,
  reviewer: 3,
  partner: 2,
  client: 1,
};

/**
 * What a caller may do in a project. Grants combine by union (a person can hold a user grant and an organization
 * grant). The server's authorization module is the only place these are enforced.
 */
export const capability = z.enum([
  /** read published revisions, their viewers and downloads */
  'read:published',
  /** read files and revisions under inbox/ that are not internal (partners) */
  'read:inbox',
  /** read every file and revision, internal ones included */
  'read:all',
  /** upload into inbox/ */
  'write:inbox',
  /** upload, modify, delete-mark any file; create revisions */
  'write:all',
  /** grant and revoke access; publish and withdraw */
  'manage',
  /** read the project's audit log */
  'read:audit',
  'comment',
]);
export type Capability = z.infer<typeof capability>;

export const roleCapabilities: Record<ProjectRole, readonly Capability[]> = {
  manager: [
    'read:published',
    'read:inbox',
    'read:all',
    'write:inbox',
    'write:all',
    'manage',
    'read:audit',
    'comment',
  ],
  editor: ['read:published', 'read:inbox', 'read:all', 'write:inbox', 'write:all', 'read:audit', 'comment'],
  reviewer: ['read:published', 'read:inbox', 'read:all', 'comment'],
  partner: ['read:published', 'read:inbox', 'write:inbox', 'comment'],
  client: ['read:published', 'comment'],
};

/** Capabilities an API key with scope "read" keeps. */
export const readCapabilities: readonly Capability[] = [
  'read:published',
  'read:inbox',
  'read:all',
  'read:audit',
];

export const principalType = z.enum(['user', 'org', 'email']);
export type PrincipalType = z.infer<typeof principalType>;

/** The folder partners may upload into. */
export const INBOX_PREFIX = 'inbox/';
